/**
 * RM-349 — the RM AI replies table (Admin Settings → RM AI replies): how the
 * page groups and searches the replies, and the lines it writes under each
 * version. Pure, so it is unit-tested (agentReplies.spec.js) rather than
 * trusted inside the page.
 *
 * The server (lib/agent/replyCatalog.js) owns the wording, the defaults, the
 * sample values, validation and the text version's length and cost; this file
 * only arranges and words what GET /agent/admin/replies and the preview return.
 */

const lower = v => String(v == null ? '' : v).toLowerCase()

/** Either version saved as an edit (the other may still be the code's wording). */
export const isEdited = reply => !!(reply?.app?.edited || reply?.text?.edited)

/**
 * Does a reply match the search? Every word typed must appear in its name
 * (`where`), its key, or either version's wording in use.
 */
export function matchesSearch(reply, query) {
  const terms = lower(query).trim().split(/\s+/).filter(Boolean)
  if (!terms.length) return true
  const fields = [reply?.where, reply?.key, reply?.app?.current, reply?.text?.current].map(lower)

  return terms.every(t => fields.some(f => f.includes(t)))
}

/**
 * The list grouped by flow, in the server's flow order (a flow it didn't list
 * goes last, in the order first seen), keeping only the replies that match the
 * search. A flow with nothing matching is left out.
 *
 * @returns {{ flow: string, replies: object[] }[]}
 */
export function groupByFlow(replies, { flows = [], query = '' } = {}) {
  const order = [...(flows || [])]
  const byFlow = new Map()
  for (const reply of replies || []) {
    if (!matchesSearch(reply, query)) continue
    const flow = reply.flow || 'Other'
    if (!order.includes(flow)) order.push(flow)
    if (!byFlow.has(flow)) byFlow.set(flow, [])
    byFlow.get(flow).push(reply)
  }

  return order.filter(f => byFlow.has(f)).map(flow => ({ flow, replies: byFlow.get(flow) }))
}

/**
 * D17 (Kapil and the product advisor, 9/30): the list grouped by capability,
 * then flow, in the server's orders. A capability or flow the server didn't
 * list goes last, in the order first seen; only matching replies are kept.
 * A flow named like its capability ("Member support") gets no sub-heading.
 *
 * @returns {{ capability: string, flows: { flow: string, replies: object[], heading: boolean }[] }[]}
 */
export function groupByCapability(replies, { capabilities = [], flows = [], query = '' } = {}) {
  const order = [...(capabilities || [])]
  const byCapability = new Map()
  for (const reply of replies || []) {
    if (!matchesSearch(reply, query)) continue
    const capability = reply.capability || 'Other'
    if (!order.includes(capability)) order.push(capability)
    if (!byCapability.has(capability)) byCapability.set(capability, [])
    byCapability.get(capability).push(reply)
  }

  return order.filter(c => byCapability.has(c)).map(capability => ({
    capability,
    flows: groupByFlow(byCapability.get(capability), { flows })
      .map(g => ({ ...g, heading: g.flow !== capability })),
  }))
}

export const segmentsLabel = n => `${Number(n) || 0} segment${Number(n) === 1 ? '' : 's'}`

export const costLabel = usd => `$${(Number(usd) || 0).toFixed(4)} per send`

/**
 * The text version's length, as sent: characters · segments · the encoding
 * (only when it isn't GSM-7) · cost per send.
 */
export function lengthLine(length) {
  if (!length) return ''
  const chars = Number(length.characters) || 0
  const parts = [`${chars} character${chars === 1 ? '' : 's'}`, segmentsLabel(length.segments)]
  if (length.encoding && length.encoding !== 'GSM-7') parts.push(`${length.encoding} encoding`)
  parts.push(costLabel(length.costPerSendUsd))

  return parts.join(' · ')
}

/** Over the segment warning? From the server's number when given, else its flag. */
export function isOverWarning(length, segmentWarning) {
  if (!length) return false
  if (Number.isFinite(segmentWarning)) return (Number(length.segments) || 0) > segmentWarning

  return !!length.overWarning
}

/** The warning under the text version, '' when it is within the warning. */
export function segmentWarningLine(length, segmentWarning) {
  return isOverWarning(length, segmentWarning) ? `Over ${segmentWarning} segments: each send costs more` : ''
}

/** Only the version(s) the admin changed, for PUT /agent/admin/replies/:key. */
export function changedVersions(reply, draft) {
  const out = {}
  if (!reply || !draft) return out
  if (draft.app !== reply.app?.current) out.app = draft.app
  if (draft.text !== reply.text?.current) out.text = draft.text

  return out
}

/** Any problem the preview reported, on either version. */
export const hasProblems = problems => !!((problems?.app || []).length || (problems?.text || []).length)

/** A version in the history: null is the code's wording. */
export const historyWording = wording => (wording == null ? '(default)' : wording)

/** History, newest first. */
export const sortHistory = versions => [...(versions || [])].sort((a, b) => (Number(b.version) || 0) - (Number(a.version) || 0))

/**
 * Put a fill-in, written {name}, into a wording at the cursor (replacing any
 * selection), or at the end when there is no cursor.
 *
 * @returns {{ value: string, caret: number }} the new wording and where the cursor goes
 */
export function insertFillIn(value, name, start, end) {
  const v = String(value == null ? '' : value)
  const token = `{${name}}`
  const s = Number.isInteger(start) ? Math.max(0, Math.min(start, v.length)) : v.length
  const e = Number.isInteger(end) ? Math.max(s, Math.min(end, v.length)) : s

  return { value: v.slice(0, s) + token + v.slice(e), caret: s + token.length }
}
